import React, { useState, useEffect, useMemo } from "react";
import { EditMenuItemDialog } from "@/components/menu/EditMenuItemDialog";
import { useToast } from "@/hooks/use-toast";
import { MenuItemCard } from "@/components/menu/MenuItemCard";
import { MenuItemsSkeleton } from "@/components/menu/MenuItemsSkeleton";
import { EmptyMenuItems } from "@/components/menu/EmptyMenuItems";
import { VirtualizedMenuItemsListWithSuspense } from "@/components/menu/VirtualizedMenuItemsList";
import { MenuItem } from "@/types/menu";
import { useTranslation } from "react-i18next";
import { useMenuStore } from "@/store/menuStore";
import { useFilteredMenuItems, useMenuCacheUtils } from "@/services/menu/menuItem/fetchMenuItems";
import { deleteMenuItem as deleteMenuItemService } from "@/services/menu/menuItem/deleteMenuItem";

interface MenuItemsListProps {
  categoryId: string;
  storeId?: string;
  isLoading?: boolean;
  searchTerm?: string;
  filteredItems?: MenuItem[];
}

// Threshold for when to use virtualization (performance optimization)
const VIRTUALIZATION_THRESHOLD = 50;

export const MenuItemsList = React.memo<MenuItemsListProps>(({ 
  categoryId, 
  storeId, 
  searchTerm,
  filteredItems: passedFilteredItems 
}) => {
  const [itemToEdit, setItemToEdit] = useState<MenuItem | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { 
    invalidateMenuCache, 
    optimisticallyRemoveMenuItem, 
    rollbackMenuCache 
  } = useMenuCacheUtils();

  const {
    toggleMenuItemAvailability,
  } = useMenuStore();

  // Use TanStack Query for menu items (only if no filtered items passed)
  const { 
    data: fetchedItems, 
    isLoading: queryLoading, 
    error: queryError
  } = useFilteredMenuItems(storeId, {
    categoryId: categoryId === 'all' ? undefined : categoryId,
  });

  // Use passed filtered items if available (for search results), otherwise use fetched items
  const filteredItems = passedFilteredItems || fetchedItems || [];
  const isLoading = queryLoading && !passedFilteredItems;

  // Determine if we should use virtualization based on item count
  const shouldUseVirtualization = useMemo(() => {
    return filteredItems.length > VIRTUALIZATION_THRESHOLD;
  }, [filteredItems.length]);

  const handleAvailabilityToggle = async (id: string, available: boolean) => {
    try {
      await toggleMenuItemAvailability(id, available);
      toast({
        title: available
          ? t("menuItemsList.itemAvailable")
          : t("menuItemsList.itemUnavailable"),
        description: available
          ? t("menuItemsList.itemAvailableDescription")
          : t("menuItemsList.itemUnavailableDescription"),
      });
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast({
        title: t("error"),
        description: t("errorTogglingAvailability"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteItem = async (id: string, name: string) => {
    try {
      // Optimistically remove the item from the cache
      if (storeId) {
        optimisticallyRemoveMenuItem(storeId, id);
      }

      // Call the service function directly instead of using Zustand store
      await deleteMenuItemService(id, storeId || "");
      
      // Invalidate cache to refresh data immediately
      if (storeId) {
        await invalidateMenuCache(storeId);
      }
      
      toast({
        title: "Menu item deleted",
        description: `${name} has been removed from the menu.`,
        variant: "destructive",
      });
    } catch (error) {
      console.error("Error deleting menu item:", error);
      
      // Revert optimistic update on error
      if (storeId) {
        rollbackMenuCache(storeId);
      }
      
      toast({
        title: t("error"),
        description: t("errorDeletingMenuItem"),
        variant: "destructive",
      });
    }
  };

  // Show loading skeleton on initial load or when loading

  if (isLoading) {
    return <MenuItemsSkeleton />;
  }

  if (!filteredItems || filteredItems.length === 0) {
    const message = searchTerm 
      ? `No menu items found matching "${searchTerm}"`
      : undefined;
    return <EmptyMenuItems message={message} />;
  }

  // Use virtualized list for large datasets
  if (shouldUseVirtualization) {
    return (
      <>
        <div className="h-[600px] w-full">
          <VirtualizedMenuItemsListWithSuspense
            items={filteredItems}
            isLoading={isLoading}
            searchTerm={searchTerm}
            categoryId={categoryId}
            onEditItem={setItemToEdit}
            onDeleteItem={handleDeleteItem}
            onToggleAvailability={handleAvailabilityToggle}
          />
        </div>

        {itemToEdit && (
          <EditMenuItemDialog
            open={!!itemToEdit}
            onOpenChange={(open) => {
              if (!open) setItemToEdit(null);
            }}
            menuItem={itemToEdit}
            onItemUpdated={() => {
              if (storeId) {
                invalidateMenuCache(storeId);
              }
            }}
          />
        )}
      </>
    );
  }

  // Use regular grid for smaller datasets
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {filteredItems.map((item) => (
          <MenuItemCard
            key={`${categoryId}-${item.id}`}
            item={item}
            onEdit={setItemToEdit}
            onDelete={handleDeleteItem}
            onToggleAvailability={handleAvailabilityToggle}
          />
        ))}
      </div>

      {itemToEdit && (
        <EditMenuItemDialog
          open={!!itemToEdit}
          onOpenChange={(open) => {
            if (!open) setItemToEdit(null);
          }}
          menuItem={itemToEdit}
          onItemUpdated={() => {
            if (storeId) {
              invalidateMenuCache(storeId);
            }
          }}
        />
      )}
    </>
  );
});

MenuItemsList.displayName = 'MenuItemsList';
